import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getDeleteChargePointDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/delete-charge-point.form-default-values';
import { DeleteChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/delete-charge-point.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoDeleteChargePointMutation } from '@service/cpms-bo-charge-point.service';

import {
  DeleteChargePointContextProvider,
  useChargePointContext,
} from '../charge-point.context';
import { ChargePointDeleteFormController } from './charge-point-delete-form/charge-point-delete-form.controller';

interface ChargePointDeleteControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const ChargePointDeleteController = ({
  onCancel,
  onSuccess,
}: ChargePointDeleteControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [deleteChargePointMutation] = useCpmsBoDeleteChargePointMutation();
  const chargePoint = useChargePointContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<Pick<DeleteChargePointModel, 'id'>> = (
    errors,
  ) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-delete.error', { error: t('global.unknown') }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<DeleteChargePointModel, 'id'>
  > = (fieldValues) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    deleteChargePointMutation({
      id: fieldValues.id,
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.charge-point-delete.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(t('$.charge-point-delete.error', { error: errorMessage }));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getDeleteChargePointDefaultValues(chargePoint.id);

  return (
    <DeleteChargePointContextProvider data={data}>
      <ChargePointDeleteFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </DeleteChargePointContextProvider>
  );
};
